import {useTranslation} from '@aboutyou/i18n';
import {styled} from '@linaria/react';
import React, {FC} from 'react';
import {COLORS} from '../../../constants/colors';
import {
  desktopAndUpLinaria,
  mediaDesktopAndUp,
  mediaMinXSBreakpoint,
  mediaTabletAndDown,
} from '../../../constants/customMedia';
import {gridCols, gridContainer} from '../../../constants/gridSystem';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {useMatchesMediaQuery} from '../../../hooks/useMatchesMediaQuery/useMatchesMediaQuery';
import {ImagePreviewType} from '../../../types/Image';
import {UnstyledButton} from '../../atoms/Buttons/UnstyledButton';
import {Text} from '../../atoms/Text';
import {BadgeList} from '../BadgeList/BadgeList';
import {BadgeKind} from '../BadgeList/types';
import {ProductImage} from '../ProductImage/ProductImage';

export type Images = Array<{
  alt?: string;
  padded?: boolean;
  src: string;
  shopTheLookLabel?: string;
}>;

const TEST_ID = 'productImageGrid';

export const ProductImageGrid: FC<{
  badgesList: BadgeKind[];
  className?: string;
  images: Images;
  /**
   * Visual state that represents inactive state for all images.
   * Can be for example used for sold out products or to put the focus on other elements.
   */
  inactive?: boolean;
  onImageClick: (index: number) => void;
  onShopTheLookClick: () => void;
  soldOut?: boolean;
  soldOutOverlayButton?: React.ReactNode;
  gridColumns?: number;
  previewImage?: ImagePreviewType;
}> = ({
  badgesList,
  className,
  images,
  inactive,
  onImageClick,
  onShopTheLookClick,
  soldOut,
  soldOutOverlayButton,
  gridColumns,
  previewImage,
}) => {
  const {t} = useTranslation();
  const badgesIndex = images.length > 1 ? 1 : 0;
  const showImagesAsInactive = inactive && !soldOut;
  const singleImage = images.length === 1;
  const isTablet = useMatchesMediaQuery(mediaTabletAndDown);

  const handleImageBtnClick = (isShowShopTheLook: boolean, index: number) => {
    if (isShowShopTheLook) {
      onShopTheLookClick();
    } else {
      onImageClick(index);
    }
  };

  return (
    <>
      <ImageList
        className={className}
        data-testid={TEST_ID}
        data-sold-out={soldOut}
      >
        {images.length &&
          images.map((image, index) => {
            const showBadges = Boolean(
              badgesList?.length && index === badgesIndex,
            );
            const showShopTheLookLabel = Boolean(image.shopTheLookLabel);
            const imageHasHighPrio = index < 2;
            const imageToUse =
              (isTablet || images.length === 1 ? index === 0 : index === 1) &&
              previewImage
                ? {
                    ...previewImage,
                    src: previewImage.url,
                    alt: previewImage.description,
                  }
                : image;

            return (
              <ImageListItem
                data-testid={`${TEST_ID}ImageItem`}
                key={`${TEST_ID}-image-${index}-${imageToUse.src}`}
                data-single-image={singleImage}
                data-large-last-item={images.length === 3 && index === 2}
              >
                <ImageButton
                  data-testid={`${TEST_ID}ImageButton`}
                  onClick={() =>
                    handleImageBtnClick(showShopTheLookLabel, index)
                  }
                  aria-label={imageToUse.alt}
                >
                  <Image
                    {...imageToUse}
                    breakpoints={gridColumns ? undefined : IMAGE_BREAKPOINTS}
                    columns={gridColumns}
                    data-testid={`${TEST_ID}Image`}
                    importance={imageHasHighPrio ? 'high' : 'low'}
                    loading={imageHasHighPrio ? 'eager' : 'lazy'}
                    $inactive={showImagesAsInactive}
                  >
                    {showShopTheLookLabel && (
                      <ShopTheLookOverlay>
                        <ShopTheLookLabel>
                          {image.shopTheLookLabel}
                        </ShopTheLookLabel>
                      </ShopTheLookOverlay>
                    )}
                    {showBadges && (
                      <BadgesWrapper isSingleImage={images?.length === 1}>
                        <BadgeList entries={badgesList} />
                      </BadgesWrapper>
                    )}
                  </Image>
                </ImageButton>
              </ImageListItem>
            );
          })}
      </ImageList>
      {soldOut && (
        <SoldOutLabelWrapper $singleImage={singleImage}>
          <Topline>{t('Unfortunately sold out')}</Topline>
          <Headline>{t('Sold Out')}</Headline>
        </SoldOutLabelWrapper>
      )}

      {soldOut && soldOutOverlayButton && (
        <ButtonWrapper>{soldOutOverlayButton}</ButtonWrapper>
      )}
    </>
  );
};

const ButtonWrapper = styled.div`
  position: sticky;
  bottom: 40px;
  margin-top: -80px;
  display: flex;
  justify-content: center;
  z-index: 1;
`;

const IMAGE_BREAKPOINTS = [
  {breakpoint: mediaDesktopAndUp, columns: 3},
  {columns: 2},
];

export const ImageList = styled.ul`
  ${gridContainer}
  flex-wrap: wrap;
  justify-content: space-between;
  padding: 0;
  width: 100%;
  position: relative;

  &[data-sold-out='true'] {
    &:before {
      content: '';

      position: absolute;
      inset: 0;
      border-radius: 4px;
      z-index: 1;
      pointer-events: none;
      background-color: rgba(0, 0, 0, 0.4);
    }
  }
`;

const HEADER_HEIGHT_ROUNDED_UP = '200px';
const Image = styled(ProductImage)<{
  $inactive?: boolean;
}>`
  box-sizing: border-box;
  width: 100%;
  height: 100%;

  &:after {
    background-color: ${COLORS.White};
    content: '';
    height: 100%;
    left: 0;
    opacity: ${({$inactive}) => ($inactive ? '0.6' : '0')};
    pointer-events: none;
    position: absolute;
    top: 0;
    width: 100%;
  }
`;

export const ImageListItem = styled.li`
  list-style: none;
  ${gridCols(3)}

  &[data-single-image='true'] {
    width: 100%;

    ${Image} {
      height: calc(100vh - ${HEADER_HEIGHT_ROUNDED_UP});
      padding-top: 0;
    }
  }

  &[data-large-last-item='true'] {
    width: 100%;
  }

  @media ${`only screen and (max-width: ${mediaMinXSBreakpoint}px)`} {
    &:nth-child(-n + 3) {
      width: 100%;
    }
    &:nth-child(4):nth-last-child(odd) {
      width: 100%;
    }
  }

  @media ${`only screen and (min-width: ${mediaMinXSBreakpoint + 1}px)`} {
    &:first-child:nth-last-child(5) ~ :nth-last-child(-n + 3) {
      ${gridCols(2)}
    }

    &:first-child:nth-last-child(7) ~ :nth-last-child(-n + 3) {
      ${gridCols(2)}
    }

    &:first-child:nth-last-child(9) ~ :nth-last-child(-n + 3) {
      ${gridCols(2)}
    }

    &:first-child:nth-last-child(10) ~ :nth-last-child(-n + 6) {
      ${gridCols(2)}
    }

    &:first-child:nth-last-child(11) ~ :nth-last-child(-n + 7) {
      ${gridCols(2)}
    }
    &:first-child:nth-last-child(11) ~ :nth-last-child(-n + 4) {
      ${gridCols(2)}
    }
  }
`;

const ImageButton = styled(UnstyledButton)`
  background-color: ${COLORS.ProductImageBackground};
  border: none;
  cursor: zoom-in;
  height: 100%;
  padding: 0;
  position: relative;
  width: 100%;
`;

const BadgesWrapper = styled.div<{isSingleImage: boolean}>`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  left: 0;
  position: absolute;
  top: ${({isSingleImage}) => (isSingleImage ? '3.25rem' : '1.25rem')};

  &[data-position-bottom='true'] {
    bottom: 2rem;
    z-index: 4;
    top: unset;
  }
`;

const ShopTheLookOverlay = styled.div`
  background-color: rgba(0, 0, 0, 0.15);
  border: none;
  cursor: pointer;
  height: 100%;
  left: 0;
  outline: none;
  position: absolute;
  top: 0;
  transition: background-color 0.3s;
  width: 100%;
  z-index: 1;

  &:hover {
    background-color: rgba(0, 0, 0, 0.225);
  }
`;

const ShopTheLookLabel = styled.span`
  border-bottom: 1.5px solid ${COLORS.White};
  color: ${COLORS.White};
  font-size: ${FONT_SIZES.px16};
  left: 50%;
  padding-bottom: 4px;
  position: absolute;
  top: 50%;
  transform: translate(-50%);
`;

const SoldOutLabelWrapper = styled.div<{$singleImage?: boolean}>`
  position: absolute;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  pointer-events: none;

  top: 0;
  left: 50%;
  aspect-ratio: 3 / 4;
  justify-content: center;
  transform: translateX(-50%);
  ${desktopAndUpLinaria(`aspect-ratio: 3 / 2;`)};

  [data-single-image='true'] & {
    top: 50%;
    left: 0;
    transform: translateY(-50%);
    justify-content: unset;
  }
`;

const Headline = styled.h3`
  color: ${COLORS.White};
  font-size: ${FONT_SIZES.px32};
  line-height: ${FONT_SIZES.px40};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  text-align: center;
`;

const Topline = styled(Text)`
  color: ${COLORS.White};
  font-size: ${FONT_SIZES.px10};
  line-height: ${FONT_SIZES.px16};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  text-align: center;
`;
